/*------------------------------------------------*/
/* LIBRARIES
/*------------------------------------------------*/
import PropTypes from 'prop-types';
import styled from 'styled-components';
import { rem } from 'polished';
/*------------------------------------------------*/
/* INTERNAL DEPENDENCIES
/*------------------------------------------------*/
import theme from '../../styles/theme';
import MediaBreakDown from '../../styles/MediaBreakDown';
import footerTopDots from '../Footer/footer-dot.png';


const Hero = styled.div`
  margin: 110px auto 77px;
  width: 100%;
  max-width: 747px;
  text-align: center;
  ${MediaBreakDown.md`
    margin: 80px auto 50px;
  `}
`;


const HeroPreHeading = styled.span`
  color: ${theme.hover.hoverBlue};
  display: block;
  font-size: 20px;
  font-weight: 700;
  line-height: 24px;
  margin: 0 0 6px 0;
`;
HeroPreHeading.displayName = 'HeroPreHeading';


const HeroHeading = styled.h1`
  font-size: 3.375rem;
  line-height: 4.05rem;
  margin-bottom: ${props => props.mb};
  margin-top: ${props => props.mt};
  ${MediaBreakDown.md`
    font-size: 1.75rem;
    line-height: 2.1rem;
  `}
`;
HeroHeading.propTypes = {
  mb: PropTypes.string,
};
HeroHeading.defaultProps = {
  mb: '0px',
};


const SectionTitle = styled.h2`
  font-size: 2.75rem;
  line-height: 3.4rem;
  margin: 0 auto ${props => props.mb} auto;

  ${props => props.maxWidth && `
    max-width: ${props.maxWidth};
  `}
  ${MediaBreakDown.lg`
    text-align: center;
  `}
  ${MediaBreakDown.md`
    font-size: 1.75rem;
    line-height: 2.1rem;
  `}
`;
SectionTitle.propTypes = {
  mb: PropTypes.string,
  maxWidth: PropTypes.string,
};
SectionTitle.defaultProps = {
  mb: '0px',
  maxWidth: '',
};


const SmallSectionTitle = styled.h2`
  font-size: 2.25rem;
  line-height: 2.7rem;
  margin-bottom: ${props => props.mb};
  ${MediaBreakDown.md`
    font-size: 1.75rem;
    line-height: 2.1rem;
  `}
`;
SmallSectionTitle.propTypes = {
  mb: PropTypes.string,
};
SmallSectionTitle.defaultProps = {
  mb: '0px',
};


const MediumTitleMobile = styled.h2`
  margin-top: ${props => props.mt};
  font-size: 1.5rem;
  line-height: 1.8rem;
`;
MediumTitleMobile.propTypes = {
  mt: PropTypes.string,
};
MediumTitleMobile.defaultProps = {
  mt: '0',
};


const SmallTitle = styled.h3`
  font-size: 1.75rem;
  line-height: 2.1rem;
  margin-top: ${props => props.mt};
  margin-bottom: ${props => props.mb};
  ${MediaBreakDown.md`
    font-size: 1.125rem;
    line-height: 1.35rem;
  `}
`;
SmallTitle.propTypes = {
  mt: PropTypes.string,
  mb: PropTypes.string,
};
SmallTitle.defaultProps = {
  mt: '0',
  mb: '18px',
};


const SectionLabel = styled.div`
  font-size: 1.125rem;
  line-height: 1.35rem;
  text-transform: uppercase;
  color: ${theme.textColor.blackText};
  margin-top: ${props => props.mt};
  margin-bottom: 6px;

  ${MediaBreakDown.lg`
    margin-top: 0;
  `}
`;
SectionLabel.propTypes = {
  mt: PropTypes.string,
};
SectionLabel.defaultProps = {
  mt: '0px',
};


const BodyText = styled.span`
  display: block;
  font-size: 1rem;
  line-height: 1.2rem;
  margin-top: ${props => props.mt};
  margin-bottom: ${props => props.mb};
`;
BodyText.propTypes = {
  mt: PropTypes.string,
  mb: PropTypes.string,
};
BodyText.defaultProps = {
  mt: '12px',
  mb: '12px',
};


const BodyTextBig = styled.p`
  font-size: 1.125rem;
  line-height: 1.8rem;
  margin: auto;
  margin-top: ${props => props.mt};
  margin-bottom: ${props => props.mb};
  width: 100%;
  max-width: ${props => props.maxWidth};

  a {
    color: ${theme.textColor.blueText};
  }
  
  br {
    margin-top: 16px;
  }
  ${MediaBreakDown.lg`
    br {
      display: none;
    }
  `}
`;
BodyTextBig.propTypes = {
  mt: PropTypes.string,
  mb: PropTypes.string,
  maxWidth: PropTypes.string,
};
BodyTextBig.defaultProps = {
  mt: '12px',
  mb: '12px',
  maxWidth: '100%',
};


const ContentWrap = styled.div`
  width: 100%;
  max-width: 900px;
  margin: auto;
  margin-bottom: ${props => props.mb};
  margin-top: ${props => props.mt};

  h2, h3, h4, h5, h6 {
    margin-bottom: ${rem('30px')};
    margin-top: ${rem('50px')};
  }

  ul, ol {
    margin: 0 40px 40px 0;
    list-style-position: inside;

    li {
      margin: 0 0 20px 0;

      /* This is a fix because "li" elements in the articles from Contentful
        comes with a "p" element inside.
       */
      p {
        display: inline;
      }
    }
  }

  ul, ol, p {
    font-size: ${rem(theme.font.paragraph.fontSize)};
    line-height: ${rem(theme.font.paragraph.lineHeight)};
  }
  ul, ol {
    margin-bottom: 40px;
  }
  p {
    margin-bottom: ${rem('25px')};;
  }

  .gatsby-image-wrapper-constrained{
    justify-content: center;
  }

  img {
    height: auto;
    display: block;
    margin: 0 auto 25px auto;
  }

  a {
    color: ${theme.textColor.blueText};
    font-size: ${rem(theme.font.paragraph.fontSize)};
    line-height: ${rem(theme.font.paragraph.lineHeight)};

    &:hover {
      color: ${theme.hover.hoverBlue};
    }
  }

  blockquote {
    background: ${theme.blockquote.dark};
    border-left: 10px solid ${theme.borderColor.blueBorder};
    margin: 1.5em 0;
    padding: 0.5em 10px;

    & > p {
      display: inline;
      margin: 0;
    }
  }
  ${MediaBreakDown.md`
    margin-bottom: ${props => (`${props.mdMb || '50px'}`)};
  `}
`;
ContentWrap.propTypes = {
  mb: PropTypes.string,
};
ContentWrap.defaultProps = {
  mb: '100px',
};


const FlexWrap = styled.div`
  display: flex;
  justify-content: space-between;
  max-width: ${props => props.maxWidth};
  align-items: ${props => props.alignItems};
  width: 100%;
  margin: auto;
  ${MediaBreakDown.lg`
    flex-wrap: wrap;
    flex-direction: ${props => props.direction};
  `}
`;
FlexWrap.propTypes = {
  maxWidth: PropTypes.string,
  alignItems: PropTypes.string,
  direction: PropTypes.string,
};
FlexWrap.defaultProps = {
  maxWidth: '100%',
  alignItems: 'flex-start',
};


const TextWrap = styled.div`
  position: relative;
  width: ${props => props.width};
  ${MediaBreakDown.lg`
    width: 100%;
    max-width: 560px;
    margin: 0 auto 20px;
  `}
`;
TextWrap.propTypes = {
  width: PropTypes.string,
};
TextWrap.defaultProps = {
  width: '50%'
};


const ImageWrap = styled.div`
  width: ${props => props.width};
  max-width: ${props => props.maxWidth};

  .gatsby-image-wrapper {
    width: 100%;
    border-radius: 6px;
    max-width: 1024px;
  }
  ${MediaBreakDown.lg`
    width: 100%;
    margin-left: auto;
    margin-right: auto;
  `}
`;
ImageWrap.propTypes = {
  width: PropTypes.string,
  maxWidth: PropTypes.string,
};
ImageWrap.defaultProps = {
  width: '50%',
  maxWidth: '100%'
};


const GraySection = styled.div`
  padding: 100px 0;
  margin: 0 16px;
  background: ${theme.body.gray};
  border-radius: 20px;

  ${props => props.padding && `
    padding: ${props.padding};
  `}

  &.no-padding-bottom {
    padding-bottom: 0;
  }
  h2 {
    text-align: ${props => props.textAlign};
  }
  .check-list {
    h2 {
      max-width: 560px;
      width: 100%;
      margin: 0px auto 40px;
    }
    p {
      max-width: 730px;
      width: 100;
      margin: 0px auto 42px;
      text-align: ${props => props.textAlign};
    }
    .gatsby-image-wrapper {
      max-width: 1024px !important;
      width: 100%;
      margin: auto;
      display: flex;
    }
    .img-wrapper {
      display: flex;
      align-items: center;
      justify-content: center;

      img {
        max-width: 1024px;
      }
    }
  }
  
  ${MediaBreakDown.lg`
    padding: 50px 0;
    margin: 0;
    border-radius: 0;
    text-align: center;

    h2 {
      text-align: center;
    }

    .check-list {
      p {
        text-align: center;
      }
    }
  `}
`;
GraySection.defaultProps = {
  textAlign: 'center',
  padding: '',
};
GraySection.propTypes = {
  textAlign: PropTypes.string,
  padding: PropTypes.string,
};


const WhiteSection = styled(GraySection)`
  position: relative;
  background: ${theme.body.dark};
`;
WhiteSection.propTypes = {
  textAlign: PropTypes.string,
};
WhiteSection.defaultProps = {
  textAlign: 'center'
};


const CTASection = styled.div`
  text-align: center;
  padding: 80px 0;

  ${SectionTitle} {
    max-width: ${props => props.maxWidth};
    margin-bottom: 50px;
    width: 100%;
  }
  p {
    max-width: ${props => props.pMaxWidth};
    width: 100%;
    margin: 14px auto 30px;
  }
  ${MediaBreakDown.lg`
    padding: 50px 0;
  `}
`;
CTASection.propTypes = {
  maxWidth: PropTypes.string,
  pMaxWidth: PropTypes.string,
};
CTASection.defaultProps = {
  maxWidth: '500px',
  pMaxWidth: '500px',
};


const DotWrap = styled.div`
  position: ${props => props.position};
  right: ${props => props.right};
  top: ${props => props.top};
  ${MediaBreakDown.xl`
    display: none;
  `}
`;
DotWrap.displayName = 'DotWrap';
DotWrap.propTypes = {
  position: PropTypes.string,
  right: PropTypes.string,
  top: PropTypes.string,
};
DotWrap.defaultProps = {
  position: 'absolute',
  right: '0',
  top: '0'
};


const SectionWrapper = styled.div`
  display: flex;
  justify-content: center;
  margin-top: 50px;
`;

const GlobalHero = styled(FlexWrap)`
  display: flex;
  justify-content: space-between;
  padding: 95px 0 50px;
  width: 100%;

  .hero-text {
    width: 50%;

    h1 {
      margin-top: 25px;
    }
    ${BodyTextBig} {
      margin: 24px auto 34px;
    }
    .button-wrap {
      display: flex;
      align-items: center;

      & a, & button {
        margin-right: 25px;
      }

      & .label {
        font-size: 14px;
        line-height: 17px;
        color: ${theme.body.light};
      }
    }
  }
  .hero-img {
    width: 47%;

    a, button {
      display: none;
    }
    .cta-wrapper {
      display: none;
    }
    .img-video-wrapper {
      position: relative;
      height: 100%;
      width: 100%;
      background: none;
      border: none;
      outline: none;
      cursor: pointer;

      .play-img {
        position: absolute;
        top: 40%;
        left: 46%;
        transform: scale(.5);
      }
    }
    .gatsby-image-wrapper {
      border-radius: 6px;
    }
  }
  ${MediaBreakDown.xl`
    .hero-text {
      width: 40%;
    }
    .hero-img {
      width: 50%;
    }
  `}
  ${MediaBreakDown.lg`
    flex-wrap: wrap;
    padding: 50px 0;

    .hero-text {
      width: 100%;
      max-width: 480px;
      margin: auto;
      text-align: center;
      h1 {
        margin: auto;
        width: 100%;
        max-width: 400px;
      }
      ${BodyTextBig} {
        margin: 24px auto;
      }
      .button-wrap {
        display: none;
      }
    }
    .hero-img {
      width: 100%;
      max-width: 90vw;
      margin: auto;
      text-align: center;

      a, button {
        display: block;
        width: 200px;
        margin: 36px auto 0;
      }
      .cta-wrapper {
        display: flex;
        justify-content: space-around;
        align-items: baseline;
        margin: 0 0 30px 0;

        a, button {
          margin: 0;
        }
      }
    }
  `}
  ${MediaBreakDown.sm`
    padding: 30px 0;

    .hero-img {
      .cta-wrapper {
        flex-direction: column;
        align-items: center;
        height: 100px;
      }
    }
  `}
`;
GlobalHero.propTypes = {
  maxWidth: PropTypes.string,
  alignItems: PropTypes.string,
  direction: PropTypes.string,
};
GlobalHero.defaultProps = {
  maxWidth: '100%',
  alignItems: 'flex-start',
};


const DotsDivider = styled.div`
  background-image: url(${footerTopDots});
  background-repeat: repeat;
  clear: both;
  height: 80px;

  ${props => props.margin && `
    margin: ${props.margin};
  `}
`;
DotsDivider.displayName = 'DotsDivider';
DotsDivider.defaultProps = {
  margin: null,
};
DotsDivider.propTypes = {
  margin: PropTypes.string,
};


const Table = styled.div`
  display: flex;
  flex-direction: column;
  ${props => props.maxWidth && `
    max-width: ${props.maxWidth};
  `}

  > div,
  > div.row {
    display: flex;
    flex-direction: row;
    border: 1px solid ${theme.borderColor.grayBorder};
    border-top: none;

    &:first-child {
      border-top: 1px solid ${theme.borderColor.grayBorder};
    }

    > div,
    > div.cell {
      text-align: center;
      border-left: 1px solid ${theme.borderColor.grayBorder};

      ${props => `
        width: calc(100% / ${props.columns});
      `}

      &:first-child {
        border-left: none;
      }
    }
  }
`;
Table.displayName = 'Table';
Table.propTypes = {
  maxWidth: PropTypes.string,
  columns: PropTypes.number,
};
Table.defaultProps = {
  maxWidth: '',
  columns: 1,
};


const SmallDarkText = styled.p`
  font-weight: 700;
  font-size: 16px;
  line-height: 19px;
  color: ${theme.textColor.blackText};
`;
SmallDarkText.displayName = 'SmallDarkText';


/*------------------------------------------------*/
/* EXPORTS
/*------------------------------------------------*/
export {
  BodyText,
  BodyTextBig,
  ContentWrap,
  CTASection,
  DotsDivider,
  DotWrap,
  FlexWrap,
  GlobalHero,
  GraySection,
  Hero,
  HeroHeading,
  HeroPreHeading,
  ImageWrap,
  MediumTitleMobile,
  SectionLabel,
  SectionTitle,
  SectionWrapper,
  SmallDarkText,
  SmallSectionTitle,
  SmallTitle,
  Table,
  TextWrap,
  WhiteSection,
};
